import * as React from 'react';
import { Link } from 'react-router-dom';
import { useCapAuthenticationService } from '@cengage/cap-portal-authentication-lib-react';
import { useCapEnvironmentsService } from '@cengage/cap-portal-environment-lib-react';

import { AppRoutingModule } from './app-routing.module';
import { AlertComponent } from './alert/alert.component';
import { useBasePathFix } from './app.hooks';
import logo from '../assets/logo-cengage.svg';
import './app.component.scss';

export function AppComponent() {
  const authenticationService = useCapAuthenticationService();
  const environmentService = useCapEnvironmentsService();
  const logoutUrl =
    'https://' +
    environmentService.getEnvironmentSetting('logoutPageHost') +
    '/logout?redirect_uri=' +
    encodeURIComponent(
      window.location.href.replace(/(http.*\/\/[^/]+)\/.*/, '$1/logout')
    );
  const logout = React.useCallback(
    (evt: React.SyntheticEvent) => {
      evt.preventDefault();
      authenticationService.clearToken();
      window.open(logoutUrl, '_self');
    },
    [authenticationService, logoutUrl]
  );
  useBasePathFix();

  return (
    <>
      <nav
        className="navbar fixed-top navbar-expand navbar-cg"
        aria-current="page"
      >
        <div
          aria-label="logo"
          className="logo"
          style={{
            // import through JS because CSS does not use <base /> tag
            backgroundImage: `url(${logo})`,
          }}
        >
          <h1 className="navbar-brand mt-0">Cengage Analytics Reporting</h1>
        </div>
        <div className="navbar-collapse small font-weight-semi-bold">
          <ul className="navbar-nav ml-auto">
            <li className="nav-item">
              <Link
                to="/"
                aria-label="go to the main page"
                className="nav-link btn-link"
                data-cy="home"
              >
                HOME
              </Link>
            </li>
            <li className="nav-item">
              <a
                href={logoutUrl}
                onClick={logout}
                aria-label="sign out of application"
                className="nav-link btn-link"
                data-cy="logout"
              >
                SIGN OUT
              </a>
            </li>
          </ul>
        </div>
      </nav>

      <div className="container-fluid main">
        <main>
          <AlertComponent />
          <AppRoutingModule />
        </main>
      </div>

      <footer className="footer mt-auto d-print-none">
        <div className="container-fluid">
          <div className="row footer-row justify-content-center font-weight-semi-bold">
            <div className="col-auto footer-text">
              &copy;{' '}
              <a
                href="https://www.cengage.com"
                target="_blank"
                className="footer-text"
                aria-label="Cengage Learning Home Page"
                rel="noreferrer"
              >
                Cengage Learning
              </a>
            </div>
            <div className="col-auto">
              <a
                href="https://www.cengage.com/copyright/"
                target="_blank"
                className="footer-text"
                aria-label="Copyright Notices"
                rel="noreferrer"
              >
                Copyright Notices
              </a>
            </div>
            <div className="col-auto">
              <a
                href="https://www.cengage.com/terms/"
                target="_blank"
                className="footer-text"
                aria-label="Terms of use"
                rel="noreferrer"
              >
                Terms of Use
              </a>
            </div>
            <div className="col-auto">
              <a
                href="https://www.cengage.com/privacy/"
                target="_blank"
                className="footer-text"
                aria-label="Privacy statement"
                rel="noreferrer"
              >
                Privacy Statement
              </a>
            </div>
            <div className="col-auto">
              <a
                href="https://www.cengage.com/accessibility/"
                target="_blank"
                className="footer-text"
                aria-label="Accessibility Statement"
                rel="noreferrer"
              >
                Accessibility Statement
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
